import React from 'react'
import { Dropdown } from 'react-bootstrap'
import { HorizontalStack, Icon } from '@ztechapps/webui.util'
import { BusyButton, BusyButtonLink } from '@ztechapps/webui.button'
import { useConfirmDialog } from '@ztechapps/common.hook'
import { useAiChatContext } from './Context'
import styles from './SessionDropdown.module.scss'

export type SessionDropdownProps = {
  className?: string
}

/**
 * Dropdown menu for managing chat sessions.
 * All session management actions are disabled while the AI is actively responding (isLoading).
 */
export const SessionDropdown = ({ className }: SessionDropdownProps) => {
  const { sessions, currentSession, createNewSession, switchSession, deleteSession, deleteAllSessions, isLoading } =
    useAiChatContext()

  // Confirmation dialog for single session deletion
  const { confirm: confirmDelete } = useConfirmDialog({
    title: 'Delete Session',
    text: 'Are you sure you want to delete this session? This action cannot be undone.',
    icon: 'warning',
    buttons: {
      confirm: {
        text: 'Delete',
      },
    },
    dangerMode: true,
  })

  // Confirmation dialog for deleting all sessions
  const { confirm: confirmDeleteAll } = useConfirmDialog({
    title: 'Delete All Sessions',
    text: 'Are you sure you want to delete all sessions? This will remove all chat history and cannot be undone.',
    icon: 'warning',
    buttons: {
      confirm: {
        text: 'Delete All',
      },
    },
    dangerMode: true,
  })

  const handleDeleteSession = async (e: React.MouseEvent, sessionId: string) => {
    e.preventDefault()
    if (isLoading) return
    await confirmDelete(() => {
      deleteSession(sessionId)
      return Promise.resolve()
    })
  }

  const handleDeleteAllSessions = async () => {
    if (isLoading) return
    await confirmDeleteAll(() => {
      deleteAllSessions()
      return Promise.resolve()
    })
  }

  const handleCreateNewSession = () => {
    if (isLoading) return
    createNewSession()
  }

  const handleSwitchSession = (sessionId: string) => {
    if (isLoading) return
    switchSession(sessionId)
  }

  const currentSessionTitle = currentSession?.title || 'New Chat'

  // Disabled item styles when loading
  const disabledStyle = isLoading ? { opacity: 0.5, cursor: 'not-allowed' } : {}

  return (
    <HorizontalStack gap={1} className="justify-between">
      <div className={`flex-grow-1 ${styles.flexTruncate}`}>
        <Dropdown className={className}>
          <Dropdown.Toggle
            disabled={isLoading}
            variant="outline-primary"
            id="session-dropdown"
            className="w-100 text-start text-truncate"
          >
            <Icon name="comments" /> {currentSessionTitle}
          </Dropdown.Toggle>

          <Dropdown.Menu style={{ maxHeight: '400px', overflowY: 'auto', minWidth: '300px' }}>
            {/* Session List */}
            {sessions.map((session) => {
              const isCurrentSession = session._id === currentSession?._id

              return (
                <Dropdown.Item
                  key={session._id}
                  onClick={() => !isCurrentSession && handleSwitchSession(session._id)}
                  active={isCurrentSession}
                  disabled={isLoading && !isCurrentSession}
                  className="d-flex justify-content-between align-items-center"
                  style={!isCurrentSession ? disabledStyle : {}}
                >
                  <span className="flex-grow-1 text-truncate">{session.title}</span>
                  <BusyButtonLink
                    bsStyle="danger"
                    className="btn-sm ms-2"
                    onClick={(e) => handleDeleteSession(e, session._id)}
                    title="Delete session"
                    isBusy={isLoading}
                    busyText=" "
                    icon="trash"
                  />
                </Dropdown.Item>
              )
            })}

            <Dropdown.Divider />

            {/* Delete All Sessions Action */}
            <Dropdown.Item
              onClick={handleDeleteAllSessions}
              className="text-danger"
              disabled={isLoading}
              style={disabledStyle}
            >
              <Icon name="trash" /> Delete All Sessions
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <div className="flex-shrink-0">
        <BusyButton
          icon="plus-circle"
          isBusy={isLoading}
          title="Create new session."
          onClick={handleCreateNewSession}
          busyText=" "
        />
      </div>
    </HorizontalStack>
  )
}
